import { Component, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { Button } from '../../button';
import { TVDFormField } from '../../form-field';
import { Input } from '../../input';
import { type ActiveDialogRef, DialogService } from '../dialog.service';

@Component({
  selector: 'tvd-prompt',
  imports: [Button, Input, TVDFormField],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="text-primary-text" data-testid="prompt-dialog">
      <h2 class="mb-4 text-lg font-semibold">{{ title() }}</h2>
      <p class="mb-4" [innerHtml]="text()"></p>

      <tvd-form-field class="mb-4">
        @if (inputLabel()) {
          <label tvdLabel>{{ inputLabel() }}</label>
        }
        @if (inputType() === 'textarea') {
          <textarea
            tvdInput
            [(value)]="inputValue"
            [placeholder]="inputPlaceholder()"
            data-testid="prompt-input"
          ></textarea>
        } @else {
          <input
            tvdInput
            [type]="inputType()"
            [(value)]="inputValue"
            [placeholder]="inputPlaceholder()"
            data-testid="prompt-input"
          />
        }
      </tvd-form-field>

      <div class="flex flex-col-reverse gap-2 md:flex-row md:justify-end">
        @if (rejectButtonText()) {
          <button
            tvdButton
            data-testid="secondary-button"
            (click)="onClose(null)"
            kind="transparent"
          >
            {{ rejectButtonText() }}
          </button>
        }
        @if (confirmButtonText()) {
          <button
            tvdButton
            kind="primary"
            data-testid="primary-button"
            (click)="onClose(inputValue())"
            [disabled]="!inputValue()"
          >
            {{ confirmButtonText() }}
          </button>
        }
      </div>
    </div>
  `,
})
export class PromptComponent implements ActiveDialogRef<string | null> {
  readonly title = input.required<string>();
  readonly text = input.required<string>();
  readonly confirmButtonText = input.required<string>();
  readonly rejectButtonText = input<string | null>(null);
  readonly inputLabel = input<string | null>(null);
  readonly inputType = input<'text' | 'password' | 'textarea'>('text');
  readonly inputPlaceholder = input<string>('');

  readonly inputValue = signal('');

  readonly #dialog = inject(DialogService).active;

  onClose(result?: string | null) {
    this.#dialog?.close(result ?? null);
  }
}
